import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  Chart,
  type ChartData,
  type ChartOptions,
} from 'chart.js/auto';

import { Store } from '@ngrx/store';
import { setCameraConfiguration, setCameraMode } from '../../../features/cesium/cesium.actions';
import { setSiteDensityHighlight } from '../../../features/site/site.actions';
import { CameraMode } from '../../../enums/camera';
import { CesiumTools } from '../../../tools/cesium.tools';
import { DetectionDensity, VolumeDensity } from '../../../models/site';
import { ActiveElement } from 'chart.js';

type Density = DetectionDensity | VolumeDensity;

@Component({
    selector: 'density-chart',
    template: `
    <div #host class="host">
      <canvas #canvas></canvas>
    </div>
  `,
    styles: [`
    :host { display:block; width:100%; height:100%; }
    .host { width:100%; height:100%; }
    canvas { width:100% !important; height:100% !important; display:block; min-height: 160px; max-height: 160px}
  `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DensityChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('host') hostRef?: ElementRef<HTMLElement>;

  @Input() data: any[] = [];
  @Input() label = '';

  private chart?: Chart;

  // hover dedupe + throttle
  private lastHoverIndex: number | null = null;
  private lastHoverDispatchTs = 0;
  private hoverEveryMs = 50; // 20 fps max

  // native handlers
  private onClickNative = (e: MouseEvent) => this.onCanvasClick(e);
  private onMoveNative = (e: MouseEvent) => this.onCanvasMove(e);
  private onLeaveNative = (_e: MouseEvent) => this.onCanvasLeave();

  constructor(private store: Store, private zone: NgZone) {}

  ngAfterViewInit(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: this.buildChartData(),
      options: this.buildOptions(),
    });

    // When created inside a freshly expanded ngIf panel, layout may settle over 1–2 frames
    this.resizeBurst();

    this.zone.runOutsideAngular(() => {
      canvas.addEventListener('click', this.onClickNative, { passive: true });
      canvas.addEventListener('mousemove', this.onMoveNative, { passive: true });
      canvas.addEventListener('mouseleave', this.onLeaveNative, { passive: true });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.chart) return;
    if (!changes['data'] && !changes['label']) return;

    // reset hover to avoid keeping a stale highlight/index after data changes
    this.lastHoverIndex = null;
    this.zone.run(() => this.store.dispatch(setSiteDensityHighlight({ density: undefined })));

    this.updateChart();
    // If the panel is mid-layout (ngIf reopen), a burst helps, cheap with <100 bars
    this.resizeBurst();
  }

  ngOnDestroy(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (canvas) {
      canvas.removeEventListener('click', this.onClickNative);
      canvas.removeEventListener('mousemove', this.onMoveNative);
      canvas.removeEventListener('mouseleave', this.onLeaveNative);
    }
    this.chart?.destroy();
  }

  // ----------------------------
  // Chart building / updating
  // ----------------------------

  private buildChartData(): ChartData {
    const src = this.data ?? [];
    const labels: any[] = new Array(src.length);
    const values: number[] = new Array(src.length);
    const colors: string[] = new Array(src.length);

    for (let i = 0; i < src.length; i++) {
      const d: any = src[i];
      labels[i] = d?.index ?? i;
      values[i] = Number(d?.count ?? 0);
      colors[i] = String(d?.colorHex ?? '#999');
    }

    return {
      labels,
      datasets: [{
        label: this.label,
        data: values,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    };
  }

  private buildOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          position: 'average',
          yAlign: 'bottom',
        }
      },
      interaction: { mode: 'nearest', intersect: true },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    };
  }

  private updateChart(): void {
    if (!this.chart) return;

    const newData = this.buildChartData();

    this.chart.data.labels = newData.labels;

    const ds = this.chart.data.datasets[0] as any;
    const newDs = newData.datasets?.[0] as any;

    ds.label = this.label;
    ds.data = newDs.data;
    ds.backgroundColor = newDs.backgroundColor;
    ds.borderColor = newDs.borderColor;

    this.chart.update('none');
  }

  // ----------------------------
  // Interactions
  // ----------------------------

  private getBarIndexFromEvent(event: MouseEvent): number | null {
    if (!this.chart) return null;

    const els = this.chart.getElementsAtEventForMode(
      event as any,
      'nearest',
      { intersect: true },
      false
    ) as ActiveElement[];

    const el = els?.[0];
    if (!el) return null;

    return (typeof el.index === 'number') ? el.index : null;
  }

  private onCanvasClick(event: MouseEvent): void {
    const index = this.getBarIndexFromEvent(event);
    if (index == null) return;

    const density = this.data?.[index] as Density | undefined;
    if (!density) return;

    this.zone.run(() => this.onClickFlyTo(density));
  }

  private onCanvasMove(event: MouseEvent): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    // throttle *before* doing hit-test (saves work on high frequency mousemove)
    const now = performance.now();
    if ((now - this.lastHoverDispatchTs) < this.hoverEveryMs) return;
    this.lastHoverDispatchTs = now;

    const idx = this.getBarIndexFromEvent(event);

    // cursor (good enough at 20fps)
    canvas.style.cursor = (idx == null) ? 'default' : 'pointer';

    // dedupe highlight dispatch
    if (idx === this.lastHoverIndex) return;
    this.lastHoverIndex = idx;

    const density = (idx == null) ? undefined : (this.data?.[idx] as Density | undefined);

    this.zone.run(() => {
      this.store.dispatch(setSiteDensityHighlight({ density }));
    });
  }

  private onCanvasLeave(): void {
    this.lastHoverIndex = null;
    const canvas = this.canvasRef?.nativeElement;
    if (canvas) canvas.style.cursor = 'default';

    this.zone.run(() => {
      this.store.dispatch(setSiteDensityHighlight({ density: undefined }));
    });
  }

  private resizeBurst(): void {
    if (!this.chart) return;

    const host = this.hostRef?.nativeElement;
    const canResize = () => {
      if (!host) return true;
      const r = host.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };

    // 2 frames are typically enough after ngIf insertion / panel animation
    requestAnimationFrame(() => {
      if (!this.chart || !canResize()) return;
      this.chart.resize();

      requestAnimationFrame(() => {
        if (!this.chart || !canResize()) return;
        this.chart.resize();
      });
    });
  }

  // ----------------------------
  // Existing behavior
  // ----------------------------

  private onClickFlyTo(density: Density): void {
    const cameraConfiguration = CesiumTools.cameraConfigurationFromTargetAndDirection(
      density.point,
      density.cameraDirection,
      density.length
    );
    if (!cameraConfiguration) return;

    this.store.dispatch(setCameraMode({ cameraMode: CameraMode.MODE_3D }));
    this.store.dispatch(setCameraConfiguration({ cameraConfiguration }));
  }
}
